import { CreateOneTimePaymentDto, FeesDTO, CreateGenericPayerDto, TransactionDTO } from "@inosio/payments-sdk";
import { createSlice } from "@reduxjs/toolkit";
import { ErrorType, RootState } from "../../../app/store";
import jwtDecode, { JwtPayload } from "jwt-decode";

export interface GenericPayerResponseDto extends CreateGenericPayerDto {
  Uuid: string;
  accessToken: string;
}

export interface GenericPayerJWTDto extends JwtPayload, CreateGenericPayerDto {
  Uuid: string;
}

type QuickPaymentsState = {
  isHandlingGenericPayer: boolean;
  isHandlingGenericPayerSuccess: boolean;
  isHandlingGenericPayerFailed: boolean;
  handlingGenericPayerError: ErrorType;
  genericPayerResponse: GenericPayerResponseDto;
  propertyAddress: string;
  isFetchingQuickPaymentFees: boolean;
  isFetchingQuickPaymentFeeSuccess: boolean;
  quickPaymentFees: FeesDTO[];
  quickPayOtpDto: CreateOneTimePaymentDto;
  selectedPaymentOption: string;
  isCreatingOtp: boolean;
  isSuccessOtp: boolean;
  errorQuickPayOtp: ErrorType;
  QPTransactionDto: TransactionDTO;
}

const initialState: QuickPaymentsState = {
  isHandlingGenericPayer: false,
  isHandlingGenericPayerSuccess: false,
  isHandlingGenericPayerFailed: false,
  handlingGenericPayerError: {} as ErrorType,
  genericPayerResponse: {} as GenericPayerResponseDto,
  propertyAddress: '',
  isFetchingQuickPaymentFees: false,
  isFetchingQuickPaymentFeeSuccess: false,
  quickPaymentFees: [],
  quickPayOtpDto: {
    PayerUuid: '',
    TotalAmount: 0,
    FeePayMethodUuid: '',
    FeeAmount: 0,
    Memo: '',
    TransactionDetails: [
      {
        Amount: 0,
        PropertyPaymentTypeId: 0,
        FromPayMethodUuid: '',
      }
    ]
  },
  selectedPaymentOption: '',
  isCreatingOtp: false,
  isSuccessOtp: false,
  errorQuickPayOtp: {} as ErrorType,
  QPTransactionDto: {} as TransactionDTO,
};

const quickPaymentsSlice = createSlice({
  name: 'quickpayments',
  initialState: initialState,
  reducers: {
    createGenericPayer(state, action) {
      state.isHandlingGenericPayer = true;
      state.isHandlingGenericPayerSuccess = false;
      state.isHandlingGenericPayerFailed = false;
      state.handlingGenericPayerError = {} as ErrorType;
    },
    updateGenericPayer(state, action) {
      state.isHandlingGenericPayer = true;
      state.isHandlingGenericPayerSuccess = false;
      state.isHandlingGenericPayerFailed = false;
      state.handlingGenericPayerError = {} as ErrorType;
    },
    setGenericPayerResponse(state, action) {
      state.isHandlingGenericPayer = false;
      state.isHandlingGenericPayerSuccess = true;
      state.isHandlingGenericPayerFailed = false;
      const decodedJWT = jwtDecode<GenericPayerJWTDto>(action.payload.accessToken);
      state.genericPayerResponse = {
        ...decodedJWT,
        accessToken: action.payload.accessToken,
      };
    },
    handleGenericPayerFailed(state, action) {
      state.isHandlingGenericPayer = false;
      state.isHandlingGenericPayerSuccess = false;
      state.isHandlingGenericPayerFailed = true;
      state.handlingGenericPayerError = action.payload;
    },
    storePropertyAddress(state, action) {
      state.propertyAddress = action.payload;
    },
    getQuickPaymentFees(state) {
      state.isFetchingQuickPaymentFees = true;
      state.isFetchingQuickPaymentFeeSuccess = false;
    },
    setQuickPaymentFees(state, action) {
      state.isFetchingQuickPaymentFees = false;
      state.isFetchingQuickPaymentFeeSuccess = true;
      state.quickPaymentFees = action.payload;
    },
    getQuickPaymentFeesFailed(state, action) {
      state.isFetchingQuickPaymentFees = false;
      state.isFetchingQuickPaymentFeeSuccess = false;
    },
    getBuildQuickPayOTPDto(state, action) {
      let temp = state.quickPayOtpDto;
      state.quickPayOtpDto = { ...temp, ...action.payload };
    },
    getSelectPayOption(state, action) {
      state.selectedPaymentOption = action.payload;
    },
    getMakeQuickPayOtp(state) {
      state.isCreatingOtp = true;
      state.isSuccessOtp = false;
      state.errorQuickPayOtp = {} as ErrorType;
    },
    setMakeQuickPayOtp(state, action) {
      state.isCreatingOtp = false;
      state.isSuccessOtp = true;
      state.QPTransactionDto = action.payload;
    },
    setMakeQuickPayOtpError(state, action) {
      state.isCreatingOtp = false;
      state.isSuccessOtp = false;
      state.errorQuickPayOtp = action.payload;
    },
    setMakeQuickPayOtpErrorClear(state) {
      state.errorQuickPayOtp = {} as ErrorType;
    },
    resetQuickPayState(state){
      return initialState;
    }
  }
})

export const {
  createGenericPayer,
  updateGenericPayer,
  setGenericPayerResponse,
  handleGenericPayerFailed,
  storePropertyAddress,
  getQuickPaymentFees,
  setQuickPaymentFees,
  getQuickPaymentFeesFailed,
  getBuildQuickPayOTPDto,
  getSelectPayOption,
  getMakeQuickPayOtp,
  setMakeQuickPayOtp,
  setMakeQuickPayOtpError,
  setMakeQuickPayOtpErrorClear,
  resetQuickPayState
} = quickPaymentsSlice.actions;

export default quickPaymentsSlice.reducer;

export const selectQuickPaymentsState = (state: RootState) => state.quickPaymentState;
export const selectGenericPayerResponse = (state: RootState) => state.quickPaymentState.genericPayerResponse;
